/**
 * UI primitives — the ONLY place base controls are styled. Pages compose
 * these; they never restyle buttons/inputs/cards ad hoc. All colors come
 * from theme tokens via Tailwind's mapped classes.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  // The gradient rather than the flat fill: the one control on every
  // screen that should look like the brand, not merely be coloured in it.
  primary:
    "bg-grad-brand text-white shadow-sm hover:opacity-90 disabled:opacity-40 disabled:hover:opacity-40",
  secondary:
    "bg-surface text-ink border border-line hover:border-brand hover:text-brand disabled:opacity-50",
  danger:
    "bg-danger text-white hover:opacity-90 disabled:opacity-50",
  ghost:
    "bg-transparent text-muted hover:text-brand disabled:opacity-50",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

export function Button({
  variant = "primary",
  loading = false,
  disabled,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-control px-4 py-2 text-sm font-semibold transition-colors ${BUTTON_STYLES[variant]} ${className}`}
      {...rest}
    >
      {loading && <Spinner size={16} />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------
export function Label(props: LabelHTMLAttributes<HTMLLabelElement>) {
  const { className = "", ...rest } = props;
  return (
    <label className={`mb-1 block text-sm font-medium text-ink ${className}`} {...rest} />
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}

export function Input({ error, className = "", ...rest }: InputProps) {
  return (
    <div>
      <input
        className={`w-full rounded-control border bg-surface px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-muted focus:border-brand ${error ? "border-danger" : "border-line"} ${className}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

/** A password field with a show/hide toggle.
 *
 * Typing a password blind is hard enough; doing it on a phone in your second
 * language is worse, and it is the most common cause of "wrong password" on a
 * sign-in form. The toggle is a real button (keyboard reachable, `aria-pressed`
 * reflects state) and never submits the form.
 *
 * Labels are passed in because primitives here are deliberately i18n-free —
 * pages hand over already-translated strings.
 */
export interface PasswordInputProps extends Omit<InputProps, "type"> {
  showLabel?: string;
  hideLabel?: string;
}

export function PasswordInput({
  error,
  className = "",
  showLabel = "Show password",
  hideLabel = "Hide password",
  ...rest
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          className={`w-full rounded-control border bg-surface py-2 pl-3 pr-11 text-sm text-ink outline-none transition-colors placeholder:text-muted focus:border-brand ${error ? "border-danger" : "border-line"} ${className}`}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          title={visible ? hideLabel : showLabel}
          className="absolute inset-y-0 right-0 grid w-11 place-items-center text-muted hover:text-ink"
        >
          <span aria-hidden>{visible ? "🙈" : "👁"}</span>
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

export function Select({ className = "", children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand ${className}`}
      {...rest}
    >
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
export type CardTone = "plain" | "brand" | "accent";

/** A coloured rail along the top edge, and a title in the matching ink.
 *  The rail is how a card says what KIND of thing it is at a glance -
 *  brand for progress and the things you do, gold for suggestions and
 *  rewards - and it is also simply where the palette lives on a screen
 *  that is otherwise white cards on a pale page. */
const CARD_TONES: Record<CardTone, { frame: string; title: string }> = {
  plain: { frame: "border-line", title: "text-ink" },
  brand: { frame: "border-line border-t-4 border-t-brand", title: "text-brand-dark" },
  accent: { frame: "border-line border-t-4 border-t-accent", title: "text-accent-dark" },
};

export function Card({
  title,
  children,
  className = "",
  actions,
  flush = false,
  tone = "plain",
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
  /** Edge-to-edge content (image/art tiles) — no padding, children own it. */
  flush?: boolean;
  tone?: CardTone;
}) {
  const look = CARD_TONES[tone];
  return (
    <section
      data-tone={tone}
      className={`rounded-card border bg-surface shadow-sm ${look.frame} ${flush ? "overflow-hidden" : "p-5"} ${className}`}
    >
      {/* flex-wrap on the header: a title beside a row of controls is the
          commonest way a card overflows a phone. Let them stack instead. */}
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
          {title && (
            <h2 className={`text-sm font-bold uppercase tracking-wide ${look.title}`}>{title}</h2>
          )}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------
type BadgeTone = "brand" | "success" | "warning" | "danger" | "neutral";

const BADGE_STYLES: Record<BadgeTone, string> = {
  brand: "bg-brand-soft text-brand-dark",
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  danger: "bg-danger/10 text-danger",
  neutral: "bg-line/60 text-muted",
};

export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE_STYLES[tone]}`}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------
export function Spinner({ size = 20 }: { size?: number }) {
  return (
    <span
      role="status"
      aria-label="loading"
      style={{ width: size, height: size }}
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// ProgressRing — the mastery circle (dashboard, practice cards)
// ---------------------------------------------------------------------------
export function ProgressRing({
  percent,
  size = 96,
  label,
}: {
  percent: number;
  size?: number;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const stroke = size / 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  return (
    <div
      role="img"
      aria-label={label ?? `${clamped}%`}
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-line"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="stroke-brand"
        />
      </svg>
      <span className="absolute text-lg font-bold text-ink">{clamped}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* max-h + scroll: the backdrop is fixed, so a dialog taller than the
          screen would otherwise have no way to reach its own buttons. */}
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-card bg-surface p-4 shadow-xl sm:p-6"
      >
        <header className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-bold text-ink">{title}</h2>
          <button aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            ✕
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------
export function EmptyState({ message }: { message: string }) {
  return <p className="py-8 text-center text-sm text-muted">{message}</p>;
}
